import React, { useState, useEffect } from 'react';
import { Plus, Link as LinkIcon, Image, Sparkles, Loader2, DollarSign, Folder, Check, ClipboardPaste } from 'lucide-react';
import { detectPlatform, formatVND, getCategoryInfo } from '../../utils/formatters';

export default function ProductForm({ categories, onAddProduct, onSuccess, prefillUrl = '' }) {
  const [targetUrl, setTargetUrl] = useState(prefillUrl || '');
  const [title, setTitle] = useState('');
  const [priceVnd, setPriceVnd] = useState('');
  const [categoryId, setCategoryId] = useState(categories[1]?.id || 2);
  const [imageUrl, setImageUrl] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isScraping, setIsScraping] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  useEffect(() => {
    if (prefillUrl && prefillUrl.trim()) {
      setTargetUrl(prefillUrl.trim());
      handleAutoExtract(prefillUrl.trim());
    }
  }, [prefillUrl]);

  const detectedPlatform = detectPlatform(targetUrl);

  // Auto-detect best matching category ID from product title
  const inferCategoryId = (text) => {
    if (!text) return null;
    const lower = text.toLowerCase();
    if (/(?:áo|quần|váy|đầm|giày|dép|thun|len|vải|quần áo|tất|vớ|hoodie|polo)/i.test(lower)) return 2; // Fashion
    if (/(?:kem|da|serum|son|sữa rửa mặt|tẩy tế bào|tẩy trang|nước hoa|body mist|mỹ phẩm|dưỡng|trắng da)/i.test(lower)) return 3; // Beauty
    if (/(?:tai nghe|bàn phím|chuột|loa|điện thoại|sạc|cáp|bluetooth|mic|laptop|pc|ipad|máy tính)/i.test(lower)) return 4; // Electronics
    if (/(?:giường|dao|kéo|nồi|chảo|thảm|khăn|nhà|bếp|gối|đệm|kệ|tủ|đèn|vệ sinh)/i.test(lower)) return 5; // Home
    if (/(?:đồng hồ|kính|mắt kính|túi|ví|balo|vòng|nhẫn|dây chuyền|khuyên)/i.test(lower)) return 6; // Accessories
    if (/(?:bánh|kẹo|trà|sữa|hạt|yến mạch|xoài|táo|bỏng|bắp|ăn vặt|đồ khô|mật)/i.test(lower)) return 7; // Food
    if (/(?:vitamin|thuốc|khẩu trang|tăm chỉ|men|probiotic|viên uống|sức khỏe|đề kháng)/i.test(lower)) return 8; // Health
    return null;
  };

  // Auto-scrape metadata from link (like Facebook link preview)
  const handleAutoExtract = async (urlToFetch) => {
    const url = urlToFetch || targetUrl;
    if (!url.trim()) return;

    setIsScraping(true);
    setStatusMessage(null);

    try {
      const res = await fetch('/api/extract-metadata', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() })
      });

      const data = await res.json();
      if (data.success) {
        if (data.title) {
          setTitle(data.title);
          const inferredCat = inferCategoryId(data.title);
          if (inferredCat) {
            setCategoryId(inferredCat);
          }
        }
        if (data.image_url) setImageUrl(data.image_url);
        if (data.price_vnd && Number(data.price_vnd) > 0) {
          setPriceVnd(String(data.price_vnd));
        } else {
          setPriceVnd('');
        }
        if (data.description && !description) setDescription(data.description);
        setStatusMessage({
          type: 'success',
          text: 'Đã tự động lấy tiêu đề và hình ảnh thực tế từ liên kết!'
        });
      } else {
        setStatusMessage({
          type: 'warning',
          text: 'Không thể tự động đọc thông tin, bạn có thể tự điền bên dưới.'
        });
      }
    } catch {
      setStatusMessage({
        type: 'warning',
        text: 'Kiểm tra đường dẫn hoặc điền thông tin thủ công.'
      });
    } finally {
      setIsScraping(false);
    }
  };

  const handleUrlBlur = () => {
    if (targetUrl.trim() && (!title || !imageUrl)) {
      handleAutoExtract(targetUrl);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !targetUrl.trim()) {
      setStatusMessage({ type: 'error', text: 'Vui lòng nhập đường link và tiêu đề sản phẩm.' });
      return;
    }

    setIsSubmitting(true);
    setStatusMessage(null);

    const numericPrice = parseInt(String(priceVnd).replace(/[^0-9]/g, ''), 10) || 0;
    const selectedCat = categories.find(c => String(c.id) === String(categoryId)) || categories[1];

    const tagList = tags
      .split(',')
      .map(t => t.trim().replace(/^#/, ''))
      .filter(Boolean);

    const productPayload = {
      title: title.trim(),
      description: description.trim() || title.trim(),
      target_url: targetUrl.trim(),
      affiliate_url: targetUrl.trim(),
      platform: detectedPlatform,
      category_id: selectedCat?.id || 2,
      category_name: selectedCat?.name || 'Thời trang',
      category_slug: selectedCat?.slug || 'fashion',
      price: formatVND(numericPrice),
      price_vnd: numericPrice,
      image_url: imageUrl.trim() || 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=500',
      tags: tagList.length > 0 ? tagList : [selectedCat?.slug || 'item', detectedPlatform]
    };

    const res = await onAddProduct(productPayload);
    setIsSubmitting(false);

    if (res && res.success) {
      setStatusMessage({ type: 'success', text: 'Thêm sản phẩm thành công!' });
      setTargetUrl('');
      setTitle('');
      setPriceVnd('');
      setImageUrl('');
      setDescription('');
      setTags('');
      if (onSuccess) onSuccess();
    } else {
      setStatusMessage({
        type: 'error',
        text: res?.error || 'Có lỗi xảy ra khi lưu sản phẩm. Vui lòng thử lại.'
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {statusMessage && (
        <div
          className={`p-3 rounded-xl text-xs flex items-center justify-between ${
            statusMessage.type === 'success'
              ? 'bg-emerald-950/80 text-emerald-200 border border-emerald-700/60'
              : statusMessage.type === 'warning'
              ? 'bg-amber-950/80 text-amber-200 border border-amber-700/60'
              : 'bg-rose-950/80 text-rose-200 border border-rose-700/60'
          }`}
        >
          <span>{statusMessage.text}</span>
          <button
            type="button"
            onClick={() => setStatusMessage(null)}
            className="text-xs font-bold px-1"
          >
            ×
          </button>
        </div>
      )}

      {/* Target URL with Auto-Fetch Button */}
      <div>
        <label className="block text-xs font-bold text-amber-200 mb-1">
          Đường dẫn sản phẩm (Shopee hoặc TikTok) *
        </label>
        <div className="flex gap-2 flex-wrap sm:flex-nowrap">
          <div className="relative flex-1 min-w-[200px]">
            <LinkIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-200/50" />
            <input
              type="url"
              required
              value={targetUrl}
              onChange={(e) => setTargetUrl(e.target.value)}
              onPaste={(e) => {
                const text = e.clipboardData?.getData('text') || '';
                if (text) {
                  const m = text.match(/https?:\/\/[^\s]+/i);
                  const url = m ? m[0] : text.trim();
                  setTargetUrl(url);
                  handleAutoExtract(url);
                }
              }}
              onBlur={handleUrlBlur}
              placeholder="Dán link Shopee hoặc TikTok vào đây..."
              className="w-full bg-[#2B0509] border border-[#520B12] rounded-xl pl-9 pr-3 py-2 text-xs text-[#FFF5E6] placeholder-amber-200/40 focus:outline-none focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20 transition"
            />
          </div>
          <button
            type="button"
            onClick={async () => {
              try {
                if (navigator.clipboard && navigator.clipboard.readText) {
                  const text = await navigator.clipboard.readText();
                  if (text && text.trim()) {
                    const m = text.trim().match(/https?:\/\/[^\s]+/i);
                    const url = m ? m[0] : text.trim();
                    setTargetUrl(url);
                    handleAutoExtract(url);
                    return;
                  }
                }
              } catch (err) {
                console.warn('Clipboard read failed:', err);
              }
            }}
            disabled={isScraping}
            className="px-2.5 py-2 bg-[#36070D] hover:bg-[#4D0B12] text-[#FFE29A] border border-[#6E141E] rounded-xl text-xs font-bold flex items-center gap-1.5 transition shrink-0"
            title="Dán nhanh từ bộ nhớ tạm và tự động chạy"
          >
            <ClipboardPaste size={13} className="text-[#F5A623]" />
            <span>Dán Clipboard</span>
          </button>
          <button
            type="button"
            onClick={() => handleAutoExtract(targetUrl)}
            disabled={isScraping || !targetUrl.trim()}
            className="px-3 py-2 bg-gradient-to-r from-[#F5A623] to-[#E58F10] hover:brightness-110 disabled:opacity-50 text-[#240306] rounded-xl text-xs font-bold flex items-center gap-1.5 transition shrink-0 shadow-sm"
          >
            {isScraping ? (
              <>
                <Loader2 size={13} className="animate-spin" />
                <span>Đang lấy...</span>
              </>
            ) : (
              <>
                <Sparkles size={13} className="text-[#240306]" />
                <span>Tự động đọc</span>
              </>
            )}
          </button>
        </div>
        <p className="text-[11px] text-amber-200/50 mt-1">
          Hệ thống sẽ tự động quét ảnh, tiêu đề và giá sản phẩm như Facebook.
        </p>
      </div>

      {/* Live Preview Card */}
      {imageUrl && (
        <div className="p-3 bg-[#2B0509] border border-[#520B12] rounded-xl flex items-center gap-3">
          <img
            src={imageUrl}
            alt="Preview"
            referrerPolicy="no-referrer"
            className="w-14 h-14 object-cover rounded-lg border border-[#6E141E]"
          />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold text-[#FFF5EB] truncate">{title || 'Tiêu đề sản phẩm'}</div>
            <div className="text-xs font-extrabold text-[#FFE29A] mt-0.5">
              {priceVnd ? formatVND(Number(priceVnd)) : 'Chưa có giá'}
            </div>
            <div className="text-[10px] text-amber-200/60 uppercase mt-0.5 font-bold">
              {detectedPlatform}
            </div>
          </div>
        </div>
      )}

      {/* Product Title */}
      <div>
        <label className="block text-xs font-bold text-amber-200 mb-1">
          Tiêu đề sản phẩm *
        </label>
        <input
          type="text"
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Tên sản phẩm..."
          className="w-full bg-[#2B0509] border border-[#520B12] rounded-xl px-3 py-2 text-xs text-[#FFF5E6] placeholder-amber-200/40 focus:outline-none focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20 transition"
        />
      </div>

      {/* Two columns: Price and Category */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Price */}
        <div>
          <label className="block text-xs font-bold text-amber-200 mb-1">
            Giá bán (VND)
          </label>
          <div className="relative">
            <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-200/50" />
            <input
              type="text"
              value={priceVnd}
              onChange={(e) => setPriceVnd(e.target.value)}
              placeholder="VD: 156000"
              className="w-full bg-[#2B0509] border border-[#520B12] rounded-xl pl-9 pr-3 py-2 text-xs text-[#FFF5E6] placeholder-amber-200/40 focus:outline-none focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20 transition"
            />
          </div>
          {priceVnd && !isNaN(Number(priceVnd)) && (
            <p className="text-[11px] text-[#FFE29A] font-bold mt-1">
              Hiển thị: {formatVND(Number(priceVnd))}
            </p>
          )}
        </div>

        {/* Category */}
        <div>
          <label className="block text-xs font-bold text-amber-200 mb-1">
            Danh mục
          </label>
          <div className="relative">
            <Folder size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-200/50" />
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(Number(e.target.value))}
              className="w-full bg-[#2B0509] border border-[#520B12] rounded-xl pl-9 pr-3 py-2 text-xs text-[#FFF5E6] focus:outline-none focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20 transition"
            >
              {categories
                .filter(c => c.slug !== 'all')
                .map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </div>
          {/* 1-Click Category Tags */}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {categories
              .filter(c => c.slug !== 'all')
              .map(c => {
                const info = getCategoryInfo(c.slug || c.name);
                const isSelected = String(categoryId) === String(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCategoryId(c.id)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition flex items-center gap-1.5 ${
                      isSelected
                        ? 'bg-gradient-to-r from-[#F5A623] to-[#E58F10] text-[#240306] font-bold shadow-xs scale-102'
                        : 'bg-[#34070B] text-amber-200/80 hover:bg-[#450A10] hover:text-[#FFF5E6] border border-[#520B12]'
                    }`}
                  >
                    <span>{info.icon}</span>
                    <span>{c.name}</span>
                    {isSelected && <Check size={11} className="text-[#240306] stroke-[3]" />}
                  </button>
                );
              })}
          </div>
        </div>
      </div>

      {/* Image URL (Optional edit) */}
      <div>
        <label className="block text-xs font-bold text-amber-200 mb-1">
          Link ảnh sản phẩm
        </label>
        <div className="relative">
          <Image size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-200/50" />
          <input
            type="url"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://..."
            className="w-full bg-[#2B0509] border border-[#520B12] rounded-xl pl-9 pr-3 py-2 text-xs text-[#FFF5E6] placeholder-amber-200/40 focus:outline-none focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20 transition"
          />
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting || !title.trim() || !targetUrl.trim()}
        className="w-full py-2.5 bg-gradient-to-r from-[#F5A623] to-[#E58F10] hover:brightness-110 disabled:opacity-50 text-[#240306] rounded-xl text-xs font-black flex items-center justify-center gap-2 transition shadow-md"
      >
        {isSubmitting ? (
          <>
            <Loader2 size={14} className="animate-spin" />
            <span>Đang lưu sản phẩm...</span>
          </>
        ) : (
          <>
            <Plus size={15} />
            <span>Thêm sản phẩm vào kho</span>
          </>
        )}
      </button>
    </form>
  );
}
